import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, ExternalLink, Sparkles, Quote, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import FaqSection from "@/components/common/FaqSection";
import HeroVideo from "@/components/common/HeroVideo";
import { PRODUCT_FAQS } from "@/data/faqs";

/**
 * Premium product landing page template.
 *
 * Uses CloudLab's *core brand* (`hsl(var(--primary))`) across every product so
 * the family looks unified. The `config` supplies content only — no per-product
 * colors. The inline mini-demo form sits in the hero (top of page); a fuller
 * "Book a Demo" section closes the page.
 *
 * When admin sets a `video_url` for the product slug (Admin → Product Videos),
 * the hero right column renders a portrait 9:16 reel instead of the image /
 * inline demo form.
 */
export default function ProductPage({ config: c }) {
  const [videoUrl, setVideoUrl] = useState("");
  useEffect(() => {
    let mounted = true;
    api.get(`/products/media/${c.slug}`)
      .then((r) => { if (mounted) setVideoUrl(r.data?.video_url || ""); })
      .catch(() => { if (mounted) setVideoUrl(""); });
    return () => { mounted = false; };
  }, [c.slug]);

  return (
    <div className="relative" data-testid={`product-page-${c.slug}`}>
      {/* HERO with inline demo */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(1100px 500px at 12% 0%, hsl(var(--primary) / 0.14) 0%, transparent 60%), radial-gradient(900px 400px at 100% 10%, hsl(var(--secondary) / 0.10) 0%, transparent 60%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 pt-16 md:pt-20 pb-10 md:pb-14">
          <Link
            to="/products"
            className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground hover:text-primary transition-colors"
          >
            ← All products
          </Link>

          <div className="mt-5 inline-flex items-center gap-2 rounded-full px-3 py-1.5 border border-border/60 bg-background/70 backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-[0.22em]">{c.kicker}</span>
          </div>

          <div className="mt-6 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <h1
                className="font-heading font-black tracking-tight leading-[0.98] text-5xl md:text-6xl lg:text-7xl"
                data-testid={`${c.slug}-hero-title`}
              >
                {c.hero.title}{" "}
                <span className="italic underline decoration-[3px] underline-offset-[6px] text-primary decoration-primary">
                  {c.hero.highlight}
                </span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
                {c.hero.subtitle}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                {c.external_url ? (
                  <a
                    href={c.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${c.slug}-hero-visit-site`}
                  >
                    <button className="group rounded-full pl-6 pr-2 py-2 inline-flex items-center gap-3 text-sm font-medium bg-primary text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)]">
                      <span>Visit official website</span>
                      <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                        <ExternalLink className="w-4 h-4" />
                      </span>
                    </button>
                  </a>
                ) : (
                  <a href="#book-demo-full" data-testid={`${c.slug}-hero-book-demo`}>
                    <button className="group rounded-full pl-6 pr-2 py-2 inline-flex items-center gap-3 text-sm font-medium bg-primary text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)]">
                      <span>Book a live demo</span>
                      <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </button>
                  </a>
                )}
              </div>

              {/* Stats */}
              {c.hero.stats?.length > 0 && (
                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl">
                  {c.hero.stats.map((s, i) => (
                    <motion.div
                      key={s.l}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 + i * 0.05, duration: 0.45 }}
                    >
                      <div className="font-heading font-black text-3xl md:text-4xl leading-none text-primary">
                        {s.v}
                      </div>
                      <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground leading-snug">
                        {s.l}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Hero right column: portrait video reel, product visual, or inline demo form */}
            <div className="lg:col-span-5">
              {videoUrl ? (
                <div className="relative mx-auto max-w-[300px] lg:max-w-[340px] rounded-[32px] overflow-hidden aspect-[9/16] border border-border shadow-[0_35px_80px_-25px_rgba(0,0,0,0.4)]">
                  <HeroVideo src={videoUrl} testid={`${c.slug}-hero-video`} />
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(15,23,42,0) 55%, rgba(15,23,42,0.55) 80%, rgba(15,23,42,0.9) 100%)",
                    }}
                  />
                  <div className="absolute inset-x-0 bottom-0 p-6 md:p-7 pointer-events-none">
                    <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-primary-foreground/85">
                      Product · CloudLab Technologies
                    </div>
                    <div className="mt-1 font-heading font-bold text-white text-xl leading-tight">
                      {c.name}
                    </div>
                  </div>
                </div>
              ) : c.external_url ? (
                <ProductVisualCard config={c} />
              ) : (
                <InlineDemoForm slug={c.slug} name={c.name} />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CLIENT LOGO MARQUEE — proof: who's using this product */}
      {c.clients?.length > 0 && <ClientStrip clients={c.clients} eyebrow={c.clientsEyebrow} />}

      {/* FEATURES */}
      {c.sections?.features && (
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeader
              kicker={c.sections.features.kicker}
              title={c.sections.features.title}
              subtitle={c.sections.features.subtitle}
            />
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {c.sections.features.items.map((f, i) => {
                const Icon = Icons[f.icon] || Icons.Sparkles;
                return (
                  <motion.div
                    key={f.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                    className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition-all hover:shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.25)]"
                    data-testid={`${c.slug}-feature-${i}`}
                  >
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-primary/10 text-primary transition-transform group-hover:scale-105">
                      <Icon className="w-5 h-5" strokeWidth={1.6} />
                    </div>
                    <div className="mt-4 font-heading font-bold text-lg leading-tight">{f.title}</div>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* MODULES */}
      {c.sections?.modules && (
        <section className="py-12 md:py-16 relative bg-muted/30">
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeader
              kicker={c.sections.modules.kicker}
              title={c.sections.modules.title}
              subtitle={c.sections.modules.subtitle}
            />
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {c.sections.modules.items.map((m, i) => (
                <motion.div
                  key={m.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                  className="rounded-2xl border border-border bg-card p-6 relative overflow-hidden hover:-translate-y-1 transition-transform"
                  data-testid={`${c.slug}-module-${i}`}
                >
                  <div className="absolute -top-2 -right-2 font-heading font-black text-[70px] leading-none opacity-10 pointer-events-none select-none text-primary">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-primary">
                    Module · {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-2 font-heading font-bold text-lg leading-tight">{m.title}</div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      {c.sections?.steps && (
        <section className="py-12 md:py-16">
          <div className="max-w-6xl mx-auto px-6">
            <SectionHeader
              kicker={c.sections.steps.kicker}
              title={c.sections.steps.title}
              subtitle={c.sections.steps.subtitle}
            />
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {c.sections.steps.items.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center font-heading font-black text-lg bg-primary text-primary-foreground">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-4 font-heading font-bold text-lg leading-tight">{s.title}</div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {c.sections?.testimonials?.items?.length > 0 && (
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeader
              kicker={c.sections.testimonials.kicker}
              title={c.sections.testimonials.title}
            />
            <div className="mt-10 grid md:grid-cols-3 gap-5">
              {c.sections.testimonials.items.map((t, i) => (
                <motion.div
                  key={t.author}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="rounded-2xl border border-border bg-card p-7 relative"
                >
                  <Quote className="w-8 h-8 opacity-25 text-primary" />
                  <p className="mt-3 text-base leading-relaxed text-foreground/90">&ldquo;{t.quote}&rdquo;</p>
                  <div className="mt-5 pt-4 border-t border-border">
                    <div className="font-heading font-bold text-sm">{t.author}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{t.role}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom conversion section: full demo form OR big "Visit website" section */}
      {c.external_url ? (
        <VisitWebsiteSection config={c} />
      ) : (
        <BookDemoSection productSlug={c.slug} productName={c.name} />
      )}

      {/* FAQ */}
      <FaqSection items={PRODUCT_FAQS[c.slug] || []} testidRoot={`${c.slug}-faq`} />
    </div>
  );
}

function SectionHeader({ kicker, title, subtitle }) {
  return (
    <div className="max-w-3xl">
      {kicker && (
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">{kicker}</div>
      )}
      <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
        {title}
      </h2>
      {subtitle && <p className="mt-3 text-base md:text-lg text-muted-foreground leading-relaxed">{subtitle}</p>}
    </div>
  );
}

// ---- CLIENT STRIP ----
function ClientStrip({ clients, eyebrow }) {
  const loop = [...clients, ...clients, ...clients];
  return (
    <section className="py-8 md:py-10 border-y border-border/60 bg-muted/40" data-testid="product-client-strip">
      <div className="max-w-7xl mx-auto px-6 mb-4 md:mb-6 flex items-center justify-between gap-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
          {eyebrow || "Trusted by leading organisations"}
        </div>
        <div className="hidden md:block h-px flex-1 bg-border/60 ml-4" />
      </div>
      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10"
             style={{ background: "linear-gradient(90deg, hsl(var(--background)) 0%, transparent 100%)" }} />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10"
             style={{ background: "linear-gradient(270deg, hsl(var(--background)) 0%, transparent 100%)" }} />
        <div className="flex gap-4 w-max animate-marquee" style={{ animationDuration: "60s" }}>
          {loop.map((cl, i) => {
            const initials = cl.name
              .split(/[^A-Za-z0-9]+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((s) => s[0].toUpperCase())
              .join("");
            return (
              <div
                key={`${cl.name}-${i}`}
                className="shrink-0 h-16 md:h-20 min-w-[220px] md:min-w-[260px] rounded-2xl bg-card border border-border/70 flex items-center gap-3 px-5"
                title={cl.name}
              >
                {cl.logo ? (
                  <img src={cl.logo} alt={cl.name} className="w-10 h-10 rounded-md object-contain" />
                ) : (
                  <div className="w-10 h-10 rounded-md bg-primary/10 text-primary font-heading font-black text-sm flex items-center justify-center shrink-0">
                    {initials}
                  </div>
                )}
                <div className="text-sm font-heading font-semibold text-foreground/85 leading-tight line-clamp-2">
                  {cl.name}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---- INLINE DEMO FORM (hero, above the fold) ----
function InlineDemoForm({ slug, name }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", organization: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await api.post("/demos", { product_slug: slug, product_name: name, ...f });
      setDone(true);
      toast.success("Demo request received. We'll be in touch.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="rounded-3xl border border-border bg-card p-6 shadow-lg" data-testid={`${slug}-inline-demo-success`}>
        <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div className="mt-4 font-heading font-bold text-xl">Thank you!</div>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
          A CloudLab specialist will reach out shortly to schedule your live demo of {name}.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="relative rounded-3xl border border-border bg-card p-6 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.25)]"
      data-testid={`${slug}-inline-demo-form`}
    >
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Sparkles className="w-4 h-4" />
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-primary">Free · 30 min</div>
          <div className="font-heading font-bold text-lg leading-tight">Get a live demo of {name}</div>
        </div>
      </div>

      <div className="mt-5 grid gap-3">
        <MiniField label="Name *" value={f.name} onChange={(v) => setF({ ...f, name: v })} testid={`${slug}-inline-name`} required />
        <MiniField label="Work email *" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid={`${slug}-inline-email`} required />
        <div className="grid grid-cols-2 gap-3">
          <MiniField label="Organisation" value={f.organization} onChange={(v) => setF({ ...f, organization: v })} testid={`${slug}-inline-org`} />
          <MiniField label="Phone / WhatsApp" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid={`${slug}-inline-phone`} />
        </div>
      </div>

      <button
        type="submit"
        disabled={busy}
        data-testid={`${slug}-inline-demo-submit`}
        className="mt-5 group w-full rounded-full pl-5 pr-2 py-2 inline-flex items-center justify-between gap-3 text-sm font-medium bg-primary text-primary-foreground disabled:opacity-70"
      >
        <span>{busy ? "Sending…" : "Book my demo"}</span>
        <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        </span>
      </button>
      <p className="mt-3 text-[11px] text-muted-foreground text-center">No credit card · No pitch · Just answers</p>
    </form>
  );
}

function MiniField({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-1.5 w-full bg-background border border-border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary/60"
      />
    </div>
  );
}

// ---- FULL BOOK-A-DEMO SECTION (bottom of page) ----
function BookDemoSection({ productSlug, productName }) {
  const [f, setF] = useState({ name: "", email: "", organization: "", phone: "", preferred_time: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const change = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await api.post("/demos", { product_slug: productSlug, product_name: productName, ...f });
      setDone(true);
      toast.success("Demo request received. We'll be in touch.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <section id="book-demo-full" className="py-14 md:py-20" data-testid={`${productSlug}-book-demo-section`}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="relative rounded-[32px] overflow-hidden p-8 md:p-14 bg-foreground text-background shadow-2xl">
          <div aria-hidden="true"
               className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full opacity-40 blur-3xl pointer-events-none bg-primary" />
          <div className="relative grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2">
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Free demo</div>
              <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-tight">
                See {productName} in action.
              </h2>
              <p className="mt-5 text-background/80 leading-relaxed max-w-md">
                A 30-minute personalised walkthrough. We&apos;ll show you the modules that matter
                for your organisation and answer every question — no sales pitch.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-background/85">
                {["Tailored to your use-case", "Live product walkthrough", "Pricing, timeline & Q&A"].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-primary" /> {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-3">
              {done ? (
                <div className="rounded-2xl bg-background/10 border border-background/20 p-8" data-testid={`${productSlug}-demo-success`}>
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <div className="mt-5 font-heading font-bold text-2xl">You&apos;re on the list.</div>
                  <p className="mt-3 text-background/80 leading-relaxed">
                    A CloudLab product specialist will reach out within one business day to schedule your live demo of {productName}.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit}
                      data-testid={`${productSlug}-demo-form`}
                      className="rounded-2xl bg-background/8 border border-background/15 backdrop-blur-sm p-6 md:p-8 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <BigField label="Your name *" value={f.name} onChange={change("name")} testid="demo-name" required />
                    <BigField label="Work email *" type="email" value={f.email} onChange={change("email")} testid="demo-email" required />
                    <BigField label="Organisation" value={f.organization} onChange={change("organization")} testid="demo-org" />
                    <BigField label="Phone / WhatsApp" value={f.phone} onChange={change("phone")} testid="demo-phone" />
                    <BigField label="Best time to contact" value={f.preferred_time} onChange={change("preferred_time")} testid="demo-time" placeholder="e.g. Weekdays 10–12 (GMT)" className="md:col-span-2" />
                  </div>
                  <div>
                    <label className="text-[11px] font-mono uppercase tracking-[0.22em] text-background/70">What would you like to see? (optional)</label>
                    <textarea rows={3} value={f.message} onChange={change("message")}
                              className="mt-2 w-full bg-background/10 border border-background/25 rounded-xl px-4 py-3 text-sm placeholder:text-background/40 focus:outline-none focus:border-background/60"
                              placeholder="Team size, must-have modules, integrations…"
                              data-testid="demo-message" />
                  </div>
                  <button type="submit" disabled={busy}
                          data-testid={`${productSlug}-demo-submit`}
                          className="group inline-flex items-center gap-3 rounded-full pl-6 pr-2 py-2 text-sm font-medium disabled:opacity-70 bg-primary text-primary-foreground">
                    <span>{busy ? "Sending…" : "Request my demo"}</span>
                    <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    </span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BigField({ label, value, onChange, type = "text", placeholder, testid, required, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[11px] font-mono uppercase tracking-[0.22em] text-background/70">{label}</label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} required={required}
             data-testid={testid}
             className="mt-2 w-full bg-background/10 border border-background/25 rounded-xl px-4 py-3 text-sm placeholder:text-background/40 focus:outline-none focus:border-background/60" />
    </div>
  );
}

// ---- PRODUCT VISUAL CARD (hero — for products with a live external site) ----
function ProductVisualCard({ config: c }) {
  const url = c.external_url;
  const cleanHost = url ? url.replace(/^https?:\/\//, "").replace(/\/$/, "") : "";
  return (
    <div
      className="relative rounded-3xl overflow-hidden border border-border shadow-[0_25px_60px_-20px_rgba(0,0,0,0.25)] group"
      data-testid={`${c.slug}-hero-visual`}
    >
      <div className="relative aspect-[4/5]">
        <img
          src={c.hero_image}
          alt={c.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
          fetchPriority="high"
        />
        {/* Bottom gradient wash for legibility */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(15,23,42,0) 55%, rgba(15,23,42,0.55) 78%, rgba(15,23,42,0.9) 100%)",
          }}
        />
        <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-primary-foreground/85">
            Live product · production
          </div>
          <div className="mt-1 font-heading font-bold text-white text-xl leading-tight">
            {c.name} is live at {cleanHost}
          </div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${c.slug}-visual-visit-site`}
            className="mt-4 group/btn inline-flex items-center gap-3 rounded-full pl-5 pr-2 py-2 text-sm font-medium bg-white text-slate-900"
          >
            <span>Visit official website</span>
            <span className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center transition-transform group-hover/btn:translate-x-0.5">
              <ExternalLink className="w-4 h-4" />
            </span>
          </a>
        </div>
      </div>
    </div>
  );
}

// ---- VISIT WEBSITE SECTION (bottom — replaces demo section for live products) ----
function VisitWebsiteSection({ config: c }) {
  const url = c.external_url;
  const cleanHost = url ? url.replace(/^https?:\/\//, "").replace(/\/$/, "") : "";
  return (
    <section className="py-14 md:py-20" data-testid={`${c.slug}-visit-section`}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="relative rounded-[32px] overflow-hidden bg-foreground text-background shadow-2xl">
          <div className="grid lg:grid-cols-2 items-stretch">
            {/* Copy */}
            <div className="relative p-8 md:p-12 lg:p-14">
              <div
                aria-hidden="true"
                className="absolute -top-24 -left-24 w-[360px] h-[360px] rounded-full opacity-40 blur-3xl pointer-events-none bg-primary"
              />
              <div className="relative">
                <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">
                  See it live
                </div>
                <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                  {c.name} is already running in production.
                </h2>
                <p className="mt-5 text-background/80 leading-relaxed max-w-md">
                  Explore the full product on its official website — sign up, take the platform for a spin,
                  and see every feature in action. It&apos;s all live at{" "}
                  <span className="text-primary">{cleanHost}</span>.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-background/85">
                  {["Full public site", "Pricing & sign-up", "Docs, guides & support"].map((t) => (
                    <li key={t} className="flex items-center gap-3">
                      <CheckCircle2 className="w-4 h-4 text-primary" /> {t}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${c.slug}-bottom-visit-site`}
                  >
                    <button className="group inline-flex items-center gap-3 rounded-full pl-6 pr-2 py-2 text-sm font-medium bg-primary text-primary-foreground">
                      <span>Visit {cleanHost}</span>
                      <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                        <ExternalLink className="w-4 h-4" />
                      </span>
                    </button>
                  </a>
                  <Link to="/contact" data-testid={`${c.slug}-bottom-contact`}>
                    <button className="rounded-full px-6 py-3 border border-background/30 text-sm font-medium hover:bg-background/10 transition-colors">
                      Talk to CloudLab
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            {/* Visual */}
            <div className="relative min-h-[320px] md:min-h-[420px]">
              <img
                src={c.hero_image}
                alt={c.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(15,23,42,0.6) 0%, rgba(15,23,42,0.2) 40%, rgba(15,23,42,0) 100%)",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
